/**
 * O que faz: Bloco de resultado por canal (anúncios, follow-up, disparos) com principal + apoios.
 * Onde usa: Seção "Resultados por canal" do dashboard, abaixo dos gráficos.
 * Conexões: apresentacaoMetricas e tipos IndicadorMetrica; sem consulta própria ao banco.
 * Tabelas: Nenhuma direta; recebe indicadores já validados por props.
 * Data: 05/09/2026 (visual compacto do design system Gens em 05/10/2026).
 */

import { ArrowUpRight } from 'lucide-react';
import { apresentarIndicador } from '@/features/metricas/apresentacaoMetricas';
import type { IndicadorMetrica } from '../../../supabase/functions/_shared/metricas/contratos';

interface ResultadoCanalDashboardProps {
  titulo: string;
  // Frase curta abaixo do número principal: diz o que ele conta (ex.: "leads de anúncios").
  legendaPrincipal?: string;
  principal: IndicadorMetrica;
  apoio: IndicadorMetrica[];
  periodoLabel: string;
  atualizadoEm: string | null;
  carregando?: boolean;
  erro?: string | null;
  onDetalhar: () => void;
}

// Nomes legíveis dos indicadores; antes a tela mostrava o id técnico com espaços ("leads agendaram").
const ROTULOS_INDICADOR: Record<string, string> = {
  leads_anuncios: 'Leads de anúncios',
  leads_agendaram: 'Leads que agendaram',
  taxa_lead_agendamento: 'Taxa de agendamento',
  vendas_registradas: 'Vendas registradas',
  leads_enviados: 'Leads que receberam',
  leads_respondidos: 'Leads que responderam',
  taxa_resposta_leads: 'Taxa de resposta',
  mensagens_enviadas: 'Mensagens enviadas',
  destinatarios_enviados: 'Destinatários',
  mensagens_entregues: 'Mensagens entregues',
  taxa_entrega: 'Taxa de entrega',
  leituras_observadas: 'Leituras',
};

export const rotuloIndicador = (id: string) => ROTULOS_INDICADOR[id] ?? id.replace(/_/g, ' ');

export function ResultadoCanalDashboard({ titulo, legendaPrincipal, principal, apoio, periodoLabel, atualizadoEm, carregando, erro, onDetalhar }: ResultadoCanalDashboardProps) {
  // Estado de carregamento/erro via props separadas; número nunca mistura estado assíncrono.
  const apresentado = apresentarIndicador(principal);
  return (
    <section aria-label={titulo} className="flex flex-col rounded-2xl border border-border bg-card p-4 shadow-[0_1px_2px_rgba(19,41,27,0.05),0_3px_10px_rgba(19,41,27,0.05)] sm:p-5">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-foreground">{titulo}</h3>
          <p className="mt-0.5 truncate text-[11px] text-muted-foreground">{periodoLabel}{atualizadoEm ? ` · às ${atualizadoEm}` : ''}</p>
        </div>
        {/* Ação no canto (seção 9.7): libera a altura que o botão largo ocupava embaixo. */}
        <button
          type="button"
          onClick={onDetalhar}
          aria-label={`Ver detalhes de ${titulo}`}
          className="-mr-1 inline-flex min-h-9 shrink-0 items-center gap-1 rounded-full px-2.5 text-xs font-semibold text-accent-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none"
        >
          Detalhes<ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </div>
      {carregando ? (
        <div className="mt-3 h-9 w-28 animate-pulse rounded-md bg-muted motion-reduce:animate-none" aria-label={`Carregando ${titulo}`} />
      ) : erro ? (
        <div className="mt-3">
          <p className="text-2xl font-semibold tabular-nums text-foreground">—</p>
          <p className="mt-1 text-xs text-muted-foreground">{erro}</p>
        </div>
      ) : (
        <div className="mt-3 flex items-baseline gap-2">
          <p className="lg-display text-[1.75rem] font-bold leading-none tabular-nums tracking-tight text-foreground">{apresentado.valor}</p>
          <p className="min-w-0 text-xs text-muted-foreground">
            {legendaPrincipal ?? rotuloIndicador(principal.id)}
            {apresentado.base && <> · <span className="tabular-nums">{apresentado.base}</span></>}
          </p>
        </div>
      )}
      {!carregando && !erro && apresentado.explicacao && apresentado.estado !== 'available' && (
        <p className="mt-1 text-xs text-muted-foreground">{apresentado.explicacao}</p>
      )}
      {/* Apoios em grade de 3 colunas: os mesmos números ocupam uma linha em vez de três. */}
      {apoio.length > 0 && <dl className="mt-4 grid grid-cols-3 gap-2 border-t border-border pt-3">
        {apoio.slice(0, 3).map((item) => (
          <div key={item.id} className="min-w-0">
            <dt className="truncate text-[11px] text-muted-foreground" title={rotuloIndicador(item.id)}>{rotuloIndicador(item.id)}</dt>
            <dd className="mt-0.5 text-sm font-semibold tabular-nums text-foreground">{apresentarIndicador(item).valor}</dd>
          </div>
        ))}
      </dl>}
    </section>
  );
}
